import {
  Tile as TileModel,
  ListDisplayType,
  List,
  Image,
  Video,
} from "@blacknut/javascript-sdk/dist";
import { logE } from "@blacknut/logging/dist";
import { AppLayout, State, StorageKey, useLayout, useTile } from "core";
import { Focusable, useSpatialNavigation } from "@blacknut/spatialnav-sdk/dist";
import clsx from "clsx";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { unstable_batchedUpdates } from "react-dom";
import { useInView } from "react-intersection-observer";
import dimens from "src/theme/dimens";
import { useTheme } from "src/theme/ThemeProvider";
import { ReactComponent as Home } from "../../assets/dist/ic_home.svg";
import { useProfiler } from "../../hooks/useProfiler";
import { useReactRouter } from "../../utils/Hooks";
import { useTileSize } from "../../utils/TileWidthProvider";
import useAnonymousCheck from "../../utils/useAnonymousCheck";
import { LOGGING_TAG } from "../../utils/Utils";
import FadeInImage from "../FadeInImage/FadeInImage";
import { usePreview } from "../Modals/PreviewContext";
import PlayIcon from "../PlayIcon/PlayIcon";
import styles from "./styles.module.scss";
import { ReactComponent as IcPass } from "../../assets/dist/ic_pass_decoration.svg";
import { ReactComponent as IcBonus } from "../../assets/dist/ic_bonus_decoration.svg";
import { useOrientation } from "src/utils/OrientationContext";
import { useGameAuthorizationFlow } from "src/utils/useGameAuthorizationFlow";
import { useSelector } from "react-redux";

interface TileProps {
  className?: string;
  item: TileModel;
  featured?: boolean;
  list: List;
  decorate?: (t: TileModel, focused: boolean) => React.ReactNode;
  onClick?: () => void;
  onPlayClicked?: () => void;
}

const Tile = ({
  className,
  item,
  featured = false,
  list,
  decorate,
  onClick: onClickFromProps,
  onPlayClicked: onPlayClickedFromProps,
}: TileProps) => {
  const { theme } = useTheme();
  const isLeMag = list.display === ListDisplayType.LEMAG;
  const list16_9 =
    list.display === ListDisplayType.FEATURED ||
    list.display === ListDisplayType.MARKETING ||
    isLeMag;

  const { active: previewActive } = usePreview();
  const { onClick, onPlayClicked } = useTile({
    item,
    list,
    ...useReactRouter(),
    previewMode: previewActive,
  });
  const { config } = useSelector((state: State) => state.globalState);
  const [videoLoaded, setVideoLoaded] = useState(false);
  let image: Image | undefined;
  let videoThumbnail: Video | undefined;
  const videoItem = useRef<HTMLVideoElement | null>(null);
  let description: string | null = null;
  const { orientation } = useOrientation();
  if (item.game) {
    const mainImages =
      item.game &&
      item.game.images &&
      item.game.images.filter((im) =>
        ["thumbnail", "thumbnail_featured", "main"].includes(im.type),
      );
    image = mainImages ? mainImages[0] : undefined;
    const videoThumbnails =
      item.game &&
      item.game.videos &&
      item.game.videos.filter((v) => v.type === "thumbnail");
    videoThumbnail = videoThumbnails ? videoThumbnails[0] : undefined;
  } else if (item.link) {
    const themedImg = item.link.images?.filter((im) => im.theme === theme.name)?.[0];
    image = themedImg || undefined;
  } else if (item.page) {
    const themedImgs = item.page[list16_9 ? "imgRectangular" : "imgSquare"] as Image[];
    const themedImg = themedImgs?.filter((im: Image) => im.theme === theme.name)?.[0];
    image = themedImg || undefined;
  } else if (item.marketing) {
    image = item.marketing.image as Image;
    description = item.marketing.name;
  } else if (item.lemag) {
    image = item.lemag.image as Image;
    description = item.lemag.name;
  } else {
    logE(LOGGING_TAG, "invalid tile: %o", item);
  }

  const [videoPlaying, setVideoPlaying] = useState(false);
  const [focused, setFocused] = useState(false);
  const [hovering, setHovering] = useState(false);
  const { active: spatialNavigationActive } = useSpatialNavigation();
  const isTVLegacy = localStorage.getItem(StorageKey.TV_LEGACY) === "true";
  const onTimeUpdate = useCallback(() => {
    if (videoItem && videoItem.current && videoItem.current.currentTime > 0.25) {
      setVideoLoaded(true);
      setVideoPlaying(true);
    }
  }, []);

  const onMouseEnter = useCallback(() => {
    setHovering(true);
  }, []);

  const onMouseLeave = useCallback(() => {
    unstable_batchedUpdates(() => {
      setFocused(false);
      setVideoLoaded(false);
      setHovering(false);
    });
  }, []);

  const onBlur = useCallback(() => {
    unstable_batchedUpdates(() => {
      setVideoLoaded(false);
      setFocused(false);
    });
  }, []);
  const onFocus = useCallback(() => {
    setFocused(true);
  }, []);

  useEffect(() => {
    if (spatialNavigationActive && hovering) {
      setHovering(false);
    }
  }, [hovering, spatialNavigationActive]);

  const layout = useLayout();
  const isWaitingTimeout: { current: NodeJS.Timeout | null } = useRef(null);
  const [longFocused, setLongFocused] = useState(false);
  useEffect(() => {
    if (layout !== AppLayout.TV) return;
    if (!focused) {
      setLongFocused(false);
      clearTimeout(isWaitingTimeout.current as NodeJS.Timeout);
    } else {
      isWaitingTimeout.current = setTimeout(() => {
        setLongFocused(true);
      }, 1000);
    }

    return () => {
      clearTimeout(isWaitingTimeout.current as NodeJS.Timeout);
    };
  }, [focused, layout]);

  const { checkForAnonymous } = useAnonymousCheck();
  const { authorize } = useGameAuthorizationFlow(item.game);
  const _onPlayClicked = useCallback(async () => {
    if (checkForAnonymous()) {
      if (await authorize()) {
        if (onPlayClickedFromProps) {
          onPlayClickedFromProps();
        } else {
          onPlayClicked();
        }
      }
    }
  }, [authorize, checkForAnonymous, onPlayClicked, onPlayClickedFromProps]);

  const [ref, inView] = useInView({
    threshold: 0,
  });
  //FIXME review this
  const { standardTileSize, featuredTileSize } = useTileSize();
  const list16_9TileWidth =
    (layout === AppLayout.PHONE || layout === AppLayout.TABLET) &&
    orientation === "LANDSCAPE"
      ? standardTileSize.width * 2 + dimens.margins.DarkRed * 10
      : 1.5 * standardTileSize.width + 0.5 * (dimens.margins.DarkRed * 10); // pixel perfect calculated from standartTileSize => 4*? + 3*(DarkRed*10) = 6*standartTileSize + 5*(DarkRed*10)
  const list16_9TileHeight = list16_9TileWidth * (9 / 16); // tiles must be 16 / 9 ratio
  const descTileHeightMobile = // tiles with a description, if height is not set, there is a bug with the scrollable list on moble
    list16_9TileHeight +
    3 * (theme?.textStyle2?.size ? theme?.textStyle2?.size : 1.4) * 10 +
    8 * 2 + // margin top/bottom
    4; // padding top
  const descTileHeight = layout === AppLayout.PHONE ? descTileHeightMobile : "100%"; // pre calculate height to get it working with scrollable Focusable

  const Root = useProfiler("tile");
  const bonusGames = !!config?.features?.blacknutplus;

  return (
    <Root>
      <Focusable
        onPressEnter={onClickFromProps || onClick}
        className={clsx([
          styles.container,
          // !WARNING! DO NOT use class "tile" for styling
          // It is purposely not scoped since it needs to be accessible from FocusableSection defaultElement prop
          "tile",
          styles.focusable,
          description && styles.imgDescContainer,
          className,
        ])}
        onMouseEnter={layout === AppLayout.DESKTOP ? onMouseEnter : undefined}
        onMouseLeave={layout === AppLayout.DESKTOP ? onMouseLeave : undefined}
        onClick={onClickFromProps || onClick}
        data-testid="container"
        style={{
          width: list16_9
            ? list16_9TileWidth
            : featured
            ? featuredTileSize.width
            : standardTileSize.width,
          height: description
            ? descTileHeight
            : list16_9
            ? list16_9TileHeight
            : featured
            ? featuredTileSize.height
            : standardTileSize.height,
        }}
        onFocus={onFocus}
        onBlur={onBlur}
      >
        <div className={clsx([styles.wrap, description && styles.noBg])} ref={ref}>
          {(inView || process.env.NODE_ENV === "test") && (
            <>
              <div className={clsx(description && styles.imgDescWrapper)}>
                <div className={styles.imageContainer}>
                  {image && (
                    <FadeInImage
                      image={image}
                      suffixOnPixelRatio1={featured ? "_md" : "_sm"}
                      className={clsx([videoLoaded ? styles.fadeIn : styles.fadeOut])}
                    />
                  )}

                  {isLeMag && (
                    <>
                      <div className={styles.leMagMask} />
                      <img
                        src={`https://images.blacknut.com/website/logo/lemag-white.svg`}
                        alt={item?.lemag?.name}
                        className={styles.leMagIcon}
                      />
                    </>
                  )}
                </div>

                {description && <p>{description}</p>}
              </div>

              {!image && (
                <div className={styles.missing} data-testid="placeholder">
                  <Home />
                </div>
              )}
              {!decorate && (
                <>
                  {!isTVLegacy &&
                    !list16_9 &&
                    (layout !== AppLayout.TV || longFocused) &&
                    videoThumbnail &&
                    ((hovering && !spatialNavigationActive) ||
                      (spatialNavigationActive && focused)) && (
                      <video
                        ref={videoItem}
                        data-testid="video"
                        className={clsx([styles.video, videoPlaying && styles.playing])}
                        muted
                        autoPlay
                        playsInline
                        onTimeUpdate={videoPlaying ? undefined : onTimeUpdate}
                        src={videoThumbnail?.url}
                        loop={true}
                      />
                    )}
                </>
              )}

              {item.game &&
                !item?.game?.maintenance &&
                !spatialNavigationActive &&
                !decorate && (
                  <PlayIcon
                    className={styles.play}
                    active={hovering}
                    onClick={_onPlayClicked}
                  />
                )}

              {bonusGames && !item.game?.addonOption?.isAvailableForCurrentUser && (
                <span className={styles.bonus} data-testid="bonus">
                  {item.game?.addonOption?.isBonusGame && <IcBonus />}
                  {!item.game?.addonOption?.isBonusGame &&
                    item.game?.addonOption?.inGamePass && <IcPass />}
                </span>
              )}

              {decorate && decorate(item, false)}
            </>
          )}
        </div>
      </Focusable>
    </Root>
  );
};
export default React.memo(Tile);
